<script setup lang="ts">
import UIInput from '@/shared/ui/Input/index';
import SearchIcon from '@/shared/ui/Icons/Search.vue';
import { useBodyScrollLock } from '@/shared/composables/useBodyScrollLock';
import { SearchResultCard } from '@/widgets/searchResultCard';
import { debounce } from '@/shared/utils/debounce';

const route = useRoute();
const { $productsService } = useNuxtApp();
const { t } = useI18n();
const { isLocked } = useBodyScrollLock(false, '.search-form-input');
const searchValue: Ref<string> = ref('');
const isShowResults: Ref<boolean> = ref(false);

const results = ref<any[]>([]);

const search = async () => {
  if (!searchValue.value) return;

  const response = await $productsService.getAllProducts({
    search: searchValue.value,
  });
  results.value = response.data.results;
};

const debouncedSearch = debounce(search, 500);

const closeSearch = () => {
  searchValue.value = '';
  isLocked.value = false;
  isShowResults.value = false;
};

watch(searchValue, () => {
  if (searchValue.value) {
    isLocked.value = true;
    isShowResults.value = true;
  } else {
    results.value = [];
    isLocked.value = false;
    isShowResults.value = false;
  }
});

watch(
  () => route.fullPath,
  () => {
    searchValue.value = '';
    isLocked.value = false;
    isShowResults.value = false;
  },
);
</script>

<template>
  <div class="search-form-input" :class="{ _active: searchValue }">
    <div v-click-outside="closeSearch" class="search-form-input__wrapper">
      <UIInput
        class="search-form-input__input"
        v-model="searchValue"
        :placeholder="t('search')"
        type="text"
        name="search"
        clearable
        @input="debouncedSearch"
      />
      <div class="search-form-input__icon-wrapper">
        <SearchIcon class="search-form-input__icon" />
      </div>
    </div>
    <Transition name="fade" :key="searchValue">
      <div v-if="isShowResults" class="search-form-input__results">
        <div class="search-form-input__result">
          <div class="search-form-input__result-title">
            <span>{{ t('resultsForSearch') }}: "{{ searchValue }}"</span>
          </div>
          <ul v-if="results.length" class="search-form-input__result-list">
            <li
              v-for="result in results"
              :key="result.id"
              class="search-form-input__result-item"
            >
              <SearchResultCard :product="result" />
            </li>
          </ul>
          <div v-if="searchValue && results.length < 1" class="search-form-input__empty">
            <span>{{ t('nothingFound') }}</span>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="less">
.search-form-input {
  display: inline-flex;
  align-items: center;
  width: 100%;
  background-color: transparent;
  border-radius: @size-x2;
  position: relative;

  &._active {
    box-shadow: 0px 0px 11px 7px #f06d2433;

    &::before {
      content: '';
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      min-height: 100vh;
      background: rgba(0, 0, 0, 0.5);
      z-index: 10;
      transition: background 0.3s ease-in-out;
    }
  }

  &__wrapper {
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    background-color: @color-tango;
    border-radius: @size-x2;
  }

  &__icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    background: @color-tango;
    border-top-right-radius: @size-x2;
    border-bottom-right-radius: @size-x2;
    width: 48px;
    height: 100%;
    position: absolute;
    right: 0;
    top: 0;
    z-index: 12;
    cursor: pointer;
  }

  &__icon {
    width: 18px;
    height: 18px;
    color: #fff;
  }

  &__input {
    width: 100%;
    z-index: 11;

    .app-input__field {
      font-size: 14px;
      line-height: 100%;
      height: 43px;
      background-color: #fff;
      border: 1px solid @color-tango;
      border-radius: @size-x2;
      box-sizing: border-box;
    }
  }

  &__result-title {
    font-size: 18px;
    line-height: 100%;
    font-weight: 500;
    color: @color-mine-shaft;
    padding: @size-x4 @size-x3;
    border-bottom: 1px solid @color-gallery;
    word-wrap: break-word;
  }

  &__result-item {
    padding: @size-x2;
    border-radius: @size-x2;
    transition: background-color @A ease-in-out;

    &:hover {
      background-color: @color-gallery;
    }
  }

  &__result {
    display: flex;
    flex-direction: column;
  }

  &__results {
    position: absolute;
    z-index: 11;
    top: calc(100% + 8px);
    left: 50%;
    width: 100%;
    background-color: #fff;
    border-radius: 8px;
    min-width: 480px;
    transform: translate(-50%, 10px);
  }

  &__result-list {
    display: flex;
    flex-direction: column;
    gap: @size-x4;
    max-height: 480px;
    overflow-y: auto;
    padding: @size-x3;
  }

  &__empty {
    font-size: 16px;
    line-height: 100%;
    font-weight: 500;
    color: @color-mine-shaft;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: @size-x5;
  }

  @media @1024 {
    &._active {
      position: absolute;
      left: @size-x4;
      width: calc(100% - @size-x8);
      z-index: 11;
    }

    &__result-item:hover {
      background-color: transparent;
    }

    &__results {
      left: 0;
      width: 100%;
      min-width: 100%;
      transform: translate(0, 10px);
    }

    &__result-list {
      max-height: calc(100vh - 220px);
    }
  }
}
</style>
